import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, X, Star, Heart, Sparkles } from 'lucide-react';

interface Exercise {
  id: number;
  type: 'fill-blank' | 'color-match' | 'number-count' | 'animal-match';
  question: string;
  options: string[];
  correct: string;
  explanation: string;
}

const exercises: Exercise[] = [
  // Simple Present & Colors
  {
    id: 1,
    type: 'fill-blank',
    question: "Betty _____ a blue door in the garden.",
    options: ["see", "sees", "seeing"],
    correct: "sees",
    explanation: "We use 'sees' because Betty is one person (she/he/it + s)"
  },
  {
    id: 2,
    type: 'color-match',
    question: "What color is Luna's horn?",
    options: ["blue", "pink", "green"],
    correct: "pink",
    explanation: "Luna the unicorn has a pink horn!"
  },
  {
    id: 3,
    type: 'number-count',
    question: "How many baby unicorns does Luna have?",
    options: ["two", "three", "four"],
    correct: "three",
    explanation: "Luna has three baby unicorns: silver, gold, and rainbow!"
  },
  {
    id: 4,
    type: 'fill-blank',
    question: "The butterflies _____ in the air.",
    options: ["dance", "dances", "dancing"],
    correct: "dance",
    explanation: "Butterflies are many, so we use 'dance' (no s)"
  },
  {
    id: 5,
    type: 'animal-match',
    question: "Spark is a friendly _____.",
    options: ["unicorn", "dragon", "butterfly"],
    correct: "dragon",
    explanation: "Spark is the green dragon with orange wings!"
  },
  {
    id: 6,
    type: 'color-match',
    question: "Spark's wings are _____.",
    options: ["orange", "purple", "silver"],
    correct: "orange",
    explanation: "Spark the dragon has orange wings!"
  },
  {
    id: 7,
    type: 'number-count',
    question: "Spark lost _____ magical gems.",
    options: ["three", "four", "five"],
    correct: "four",
    explanation: "Spark lost four gems: red, blue, yellow, and green!"
  },
  {
    id: 8,
    type: 'fill-blank',
    question: "Betty _____ happy when she finds new friends.",
    options: ["is", "are", "am"],
    correct: "is",
    explanation: "Betty is one person, so we use 'is'"
  }
];

const vocabularyMatching = [
  { english: "happy", french: "heureux/heureuse", emoji: "😊" },
  { english: "magical", french: "magique", emoji: "✨" },
  { english: "garden", french: "jardin", emoji: "🌸" },
  { english: "unicorn", french: "licorne", emoji: "🦄" },
  { english: "dragon", french: "dragon", emoji: "🐉" },
  { english: "butterfly", french: "papillon", emoji: "🦋" },
  { english: "friends", french: "amis", emoji: "👫" },
  { english: "beautiful", french: "beau/belle", emoji: "🌟" }
];

export const GrammarExercises: React.FC = () => {
  const [currentExercise, setCurrentExercise] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [completedExercises, setCompletedExercises] = useState<number[]>([]);
  
  // Vocabulary matching state
  const [matchingPairs, setMatchingPairs] = useState<{[key: string]: string}>({});
  const [selectedEnglish, setSelectedEnglish] = useState<string>('');

  const currentEx = exercises[currentExercise];

  const handleAnswer = () => {
    setShowResult(true);
    if (selectedAnswer === currentEx.correct) {
      setScore(score + 1);
      setCompletedExercises([...completedExercises, currentEx.id]);
    }
  };

  const nextExercise = () => {
    if (currentExercise < exercises.length - 1) {
      setCurrentExercise(currentExercise + 1);
      setSelectedAnswer('');
      setShowResult(false);
    }
  };

  const resetExercises = () => {
    setCurrentExercise(0);
    setSelectedAnswer('');
    setShowResult(false);
    setScore(0);
    setCompletedExercises([]);
  };

  const getExerciseIcon = (type: string) => {
    switch (type) {
      case 'fill-blank': return '📝';
      case 'color-match': return '🌈';
      case 'number-count': return '🔢';
      case 'animal-match': return '🦄';
      default: return '⭐';
    }
  };

  const handleVocabularyMatch = (english: string, french: string) => {
    if (selectedEnglish === english) {
      setMatchingPairs({...matchingPairs, [english]: french});
      setSelectedEnglish('');
    } else {
      setSelectedEnglish(english);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Grammar Exercises */}
      <Card className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="text-2xl text-center text-blue-600 flex items-center justify-center gap-2">
            <Star className="w-6 h-6" />
            Betty's Magic Grammar Games
            <Star className="w-6 h-6" />
          </CardTitle>
          <div className="text-center">
            <Badge className="mr-2 bg-purple-100 text-purple-700">
              Exercise {currentExercise + 1} of {exercises.length}
            </Badge>
            <Badge className="bg-green-100 text-green-700">
              Score: {score}/{exercises.length}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {currentExercise < exercises.length ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="text-4xl mb-2">{getExerciseIcon(currentEx.type)}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  {currentEx.question}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {currentEx.options.map((option, index) => (
                  <Button
                    key={index}
                    variant={selectedAnswer === option ? "default" : "outline"}
                    size="lg"
                    onClick={() => setSelectedAnswer(option)}
                    disabled={showResult}
                    className={`h-16 text-lg ${
                      showResult 
                        ? option === currentEx.correct 
                          ? 'bg-green-100 border-green-500 text-green-700' 
                          : option === selectedAnswer 
                            ? 'bg-red-100 border-red-500 text-red-700'
                            : ''
                        : selectedAnswer === option
                          ? 'bg-purple-500 text-white'
                          : 'bg-white hover:bg-purple-50'
                    }`}
                  >
                    {option}
                  </Button>
                ))}
              </div>

              {showResult && (
                <div className={`p-4 rounded-lg border-2 ${
                  selectedAnswer === currentEx.correct 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {selectedAnswer === currentEx.correct ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <X className="w-5 h-5 text-red-600" />
                    )}
                    <span className={`font-semibold ${
                      selectedAnswer === currentEx.correct ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {selectedAnswer === currentEx.correct ? 'Correct! Well done!' : 'Not quite right!'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{currentEx.explanation}</p>
                </div>
              )}

              <div className="flex justify-center gap-3">
                {!showResult ? (
                  <Button
                    onClick={handleAnswer}
                    disabled={!selectedAnswer}
                    className="bg-purple-500 hover:bg-purple-600 text-white px-8"
                  >
                    Check Answer
                  </Button>
                ) : (
                  <>
                    {currentExercise < exercises.length - 1 ? (
                      <Button
                        onClick={nextExercise}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-8"
                      >
                        Next Exercise →
                      </Button>
                    ) : (
                      <Button
                        onClick={resetExercises}
                        className="bg-green-500 hover:bg-green-600 text-white px-8"
                      >
                        🎉 Play Again!
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-purple-600 mb-2">
                Congratulations!
              </h3>
              <p className="text-lg text-gray-700 mb-4">
                You completed all exercises! Final score: {score}/{exercises.length}
              </p>
              <Button
                onClick={resetExercises}
                className="bg-purple-500 hover:bg-purple-600 text-white px-8"
              >
                Play Again!
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vocabulary Matching Game */}
      <Card className="bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50">
        <CardHeader>
          <CardTitle className="text-2xl text-center text-orange-600 flex items-center justify-center gap-2">
            <Heart className="w-6 h-6" />
            Magic Word Matching
            <Heart className="w-6 h-6" />
          </CardTitle>
          <p className="text-center text-gray-600">Match English words with French words!</p>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* English Words */}
            <div>
              <h4 className="font-semibold text-blue-700 mb-3 text-center">🇬🇧 English</h4>
              <div className="space-y-2">
                {vocabularyMatching.map((item, index) => (
                  <Button
                    key={index}
                    variant={selectedEnglish === item.english ? "default" : "outline"}
                    onClick={() => setSelectedEnglish(item.english)}
                    disabled={matchingPairs[item.english]}
                    className={`w-full justify-start text-left h-12 ${
                      matchingPairs[item.english] 
                        ? 'bg-green-100 border-green-300 text-green-700' 
                        : selectedEnglish === item.english
                          ? 'bg-blue-500 text-white'
                          : 'bg-white hover:bg-blue-50'
                    }`}
                  >
                    <span className="mr-2">{item.emoji}</span>
                    {item.english}
                    {matchingPairs[item.english] && <CheckCircle className="w-4 h-4 ml-auto" />}
                  </Button>
                ))}
              </div>
            </div>

            {/* French Words */}
            <div>
              <h4 className="font-semibold text-red-700 mb-3 text-center">🇫🇷 Français</h4>
              <div className="space-y-2">
                {vocabularyMatching.map((item, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    onClick={() => handleVocabularyMatch(selectedEnglish, item.french)}
                    disabled={Object.values(matchingPairs).includes(item.french)}
                    className={`w-full justify-start text-left h-12 ${
                      Object.values(matchingPairs).includes(item.french)
                        ? 'bg-green-100 border-green-300 text-green-700'
                        : 'bg-white hover:bg-red-50'
                    }`}
                  >
                    <span className="mr-2">{item.emoji}</span>
                    {item.french}
                    {Object.values(matchingPairs).includes(item.french) && <CheckCircle className="w-4 h-4 ml-auto" />}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {Object.keys(matchingPairs).length === vocabularyMatching.length && (
            <div className="mt-6 text-center p-4 bg-green-50 border-2 border-green-200 rounded-lg">
              <Sparkles className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <h3 className="text-xl font-bold text-green-700">Perfect Match! 🎉</h3>
              <p className="text-green-600">You matched all the words correctly!</p>
              <Button
                onClick={() => {
                  setMatchingPairs({});
                  setSelectedEnglish('');
                }}
                className="mt-3 bg-green-500 hover:bg-green-600 text-white"
              >
                Play Again!
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};